import React from 'react';

import { Link } from '~/lib/react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Chip, Stack, Tooltip, Typography } from '@mui/material';

import {
  formatScheduleWindow,
  isBundleCurrentlyLive,
  isCampaignCurrentlyLive,
} from '~/components/Accommodation/Pages/Properties/components/attachedPromotions';

import { AttachedBundle, AttachedCampaign } from '~/services/AccommodationService';

interface Props {
  attachedBundles: Array<AttachedBundle>;
  attachedCampaigns: Array<AttachedCampaign>;
}

interface PromotionEntry {
  id: string;
  name: string;
  kind: 'Bundle' | 'Campaign';
  to: string;
  isLive: boolean;
  scheduleWindow: string | null;
}

const toBundleEntry = (bundle: AttachedBundle): PromotionEntry => {
  const isLive = isBundleCurrentlyLive(bundle);

  return {
    id: bundle.id,
    name: bundle.name,
    kind: 'Bundle',
    to: `/accommodation/bundles/${bundle.id}/hotels`,
    isLive,
    scheduleWindow: formatScheduleWindow(bundle.schedules),
  };
};

const toCampaignEntry = (campaign: AttachedCampaign): PromotionEntry => {
  const isLive = isCampaignCurrentlyLive(campaign);

  return {
    id: campaign.id,
    name: campaign.displayName,
    kind: 'Campaign',
    to: `/accommodation/campaigns/${campaign.id}`,
    isLive,
    scheduleWindow: formatScheduleWindow(campaign.schedules),
  };
};

/**
 * Collapses every attached bundle and campaign into a single header chip, because promotion names
 * are long enough that rendering one chip each pushes the rest of the header actions onto new rows.
 * The hover panel keeps each promotion clickable, so navigating to a bundle or campaign still takes
 * a single click.
 */
export default function AccommodationPropertyPromotionsSummary({ attachedBundles, attachedCampaigns }: Props) {
  const entries = [...attachedBundles.map(toBundleEntry), ...attachedCampaigns.map(toCampaignEntry)];

  if (entries.length === 0) {
    return null;
  }

  const liveCount = entries.filter((entry) => entry.isLive).length;

  const panel = (
    <Stack spacing={1} p={0.5}>
      {entries.map((entry) => (
        <Stack key={`${entry.kind}:${entry.id}`} spacing={0.25} alignItems="flex-start">
          <Chip
            icon={entry.kind === 'Bundle' ? <LocalOfferIcon /> : <CampaignIcon />}
            label={entry.name}
            color={entry.isLive ? 'success' : 'default'}
            variant="outlined"
            size="small"
            clickable
            component={Link}
            to={entry.to}
            sx={{ maxWidth: '100%' }}
          />
          {entry.scheduleWindow && (
            <Typography variant="caption" color="text.secondary">
              {entry.scheduleWindow}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );

  return (
    // leaveDelay keeps the panel open while the pointer travels from the chip onto it, so the
    // promotion links stay reachable.
    <Tooltip
      title={panel}
      leaveDelay={300}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 420,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: 3,
          },
        },
      }}
    >
      <Chip
        icon={<LocalOfferIcon />}
        label={`${entries.length} ${entries.length === 1 ? 'promotion' : 'promotions'}${liveCount > 0 ? ` · ${liveCount} live` : ''}`}
        color={liveCount > 0 ? 'success' : 'default'}
        variant="outlined"
        size="small"
      />
    </Tooltip>
  );
}
